import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Star, Trash2, Wallet } from "lucide-react";
import client, { setToken } from "@/lib/api";
import { Switch } from "@/components/ui/switch";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { WalletPicker } from "@/components/wallet/WalletPicker";
import { StepUpDialog } from "@/components/wallet/StepUpDialog";
import { RecoveryForm } from "@/components/account/RecoveryForm";
import { signWalletChallenge, walletErrorMessage } from "@/lib/wallet";

export function WalletsSection() {
  const [wallets, setWallets] = useState([]);
  const [methods, setMethods] = useState([]);
  const [picker, setPicker] = useState(false);
  const [busy, setBusy] = useState(false);
  const [pending, setPending] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const [conflict, setConflict] = useState(false);

  const load = useCallback(() => {
    client.get("/account/wallets").then((r) => setWallets(r.data.wallets));
    client.get("/account/security").then((r) => setMethods(r.data.methods));
  }, []);
  useEffect(load, [load]);

  // Runs a sensitive action; if the server asks for re-authentication, confirm identity then retry.
  const guarded = async (fn) => {
    try { await fn(); }
    catch (e) {
      if (e.response?.status === 403 && e.response?.data?.detail === "reauth_required") setPending(() => fn);
      else if (e.response?.status === 409 && e.response?.data?.detail === "wallet_conflict") setConflict(true);
      else toast.error(walletErrorMessage(e));
    }
  };
  const applyResult = (data, msg) => { if (data.token) setToken(data.token); setWallets(data.wallets); load(); if (msg) toast.success(msg); };

  const link = async (provider) => {
    setBusy(true); setConflict(false);
    await guarded(async () => {
      const signed = await signWalletChallenge(provider, "link");
      const { data } = await client.post("/auth/wallet/link", signed);
      setPicker(false);
      applyResult(data, data.already_linked ? "This wallet is already linked" : "Wallet linked");
    });
    setPicker(false); setBusy(false);
  };
  const patch = (w, body) => guarded(async () => applyResult((await client.patch(`/account/wallets/${w.id}`, body)).data));
  const unlink = (w) => guarded(async () => applyResult((await client.post(`/account/wallets/${w.id}/unlink`, { confirm: true })).data, "Wallet removed"));

  return (
    <div className="space-y-6" data-testid="wallets-section">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h2 className="font-display text-lg font-bold">Linked wallets</h2>
          <p className="text-sm text-zinc-400 mt-1 max-w-xl">Sign in with any linked wallet and keep the same account, memberships and history. Linking only needs a signature: no transaction, no gas.</p>
        </div>
        <button data-testid="link-wallet-btn" onClick={() => setPicker(true)} className="flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#ccff00] text-black font-semibold text-sm">
          <Plus className="w-4 h-4" /> Link wallet
        </button>
      </div>
      {conflict && <RecoveryForm onClose={() => setConflict(false)} />}
      {wallets.length === 0 && <p data-testid="wallets-empty" className="text-sm text-zinc-500 border border-dashed border-white/10 rounded-xl p-6">No wallets linked yet.</p>}
      <div className="space-y-3">
        {wallets.map((w) => (
          <div key={w.id} data-testid={`wallet-row-${w.id}`} className="flex flex-wrap items-center gap-4 rounded-xl border border-white/10 bg-white/[0.02] p-4">
            <Wallet className="w-5 h-5 text-[#ccff00]" />
            <div className="min-w-0 flex-1">
              <p className="font-mono-cl text-sm break-all" data-testid="wallet-address">{w.address}</p>
              <input defaultValue={w.label} placeholder="Add a label" maxLength={40} data-testid="wallet-label"
                onBlur={(e) => e.target.value !== w.label && patch(w, { label: e.target.value })}
                className="bg-transparent text-xs text-zinc-400 focus:outline-none focus:text-zinc-200 mt-1" />
              <p className="text-[11px] text-zinc-600">Verified on {w.chain_name} · linked {w.linked_at?.slice(0, 10)}</p>
            </div>
            <button data-testid="wallet-primary-btn" onClick={() => !w.is_primary && patch(w, { is_primary: true })} title="Primary wallet"
              className={`flex items-center gap-1 text-xs ${w.is_primary ? "text-[#ccff00]" : "text-zinc-500 hover:text-zinc-200"}`}>
              <Star className="w-3.5 h-3.5" fill={w.is_primary ? "currentColor" : "none"} /> {w.is_primary ? "Primary" : "Make primary"}
            </button>
            <label className="flex items-center gap-2 text-xs text-zinc-400">Sign-in
              <Switch data-testid="wallet-signin-toggle" checked={w.auth_enabled} onCheckedChange={(v) => patch(w, { auth_enabled: v })} /></label>
            <button data-testid="wallet-unlink-btn" onClick={() => setConfirm(w)} className="text-zinc-500 hover:text-red-400"><Trash2 className="w-4 h-4" /></button>
          </div>
        ))}
      </div>
      <WalletPicker open={picker} onOpenChange={setPicker} onPick={link} busy={busy} title="Link a wallet" />
      <StepUpDialog open={!!pending} methods={methods} onClose={() => setPending(null)}
        onDone={() => { const fn = pending; setPending(null); guarded(fn); }} />
      <AlertDialog open={!!confirm} onOpenChange={(o) => !o && setConfirm(null)}>
        <AlertDialogContent className="bg-[#0b0b0b] border-white/10">
          <AlertDialogHeader>
            <AlertDialogTitle>Remove this wallet?</AlertDialogTitle>
            <AlertDialogDescription className="font-mono-cl break-all">{confirm?.address}</AlertDialogDescription>
          </AlertDialogHeader>
          <p className="text-sm text-zinc-400">It will no longer sign in to your account and other devices will be signed out. Memberships and payment history stay on your account.</p>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="wallet-unlink-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="wallet-unlink-confirm" className="bg-red-500 hover:bg-red-600" onClick={() => { const w = confirm; setConfirm(null); unlink(w); }}>Remove wallet</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
